<template>
  <section class="section-base form-section-base form-base">
    <div class="form-section-base__title">
      Сведения о заявителе, являющимся заинтересованным лицом
    </div>
    <v-container>
      <div class="form-section-base__controls">
        <v-row>
          <v-col>
            <v-radio-group v-model="role" row>
              <v-radio
                label="Физическое лицо"
                :value="Role.PERSON"
                :disabled="roleType !== Role.PERSON"
              />
              <v-radio
                label="Юридическое лицо"
                :value="Role.ORGANIZATION"
                :disabled="isOrganizationDisabled"
              />
              <v-radio
                label="Иностранное юридическое лицо"
                :value="Role.FOREIGN_ORGANIZATION"
                :disabled="isForeignOrganizationDisabled"
              />
            </v-radio-group>
          </v-col>
        </v-row>
      </div>

      <MrpaDisablePerson
        v-if="role === Role.PERSON"
        :v="$v.person"
        :disabled="disabled"
      />

      <MrpaDisableOrganization
        v-if="role === Role.ORGANIZATION"
        :v="$v.organization"
        :disabled="disabled"
      />

      <MrpaDisableForeignOrganization
        v-if="role === Role.FOREIGN_ORGANIZATION"
        :v="$v.foreignOrganization"
        :disabled="disabled"
      />

      <Footer
        @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
        first-step
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';
import { Role } from '@/utils/dictionaries';
import { checkInvalid, leaveStep } from '@/utils/services';
import { ServicesModule } from '@/store/modules/services';
import Footer from '@/components/services/elements/Footer';
import { DocumentType, StepDirection, StepName } from '@/types/services';
import MrpaDisablePerson, {
  mrpaDisablePersonModel,
  getMrpaDisablePersonValidation,
  prefillEsiaMrpaDisablePerson,
} from '@/components/services/EgrnConfirm/Applicant/MrpaDisable/MrpaDisablePerson';
import MrpaDisableOrganization, {
  getMrpaDisableOrganizationValidation,
  mrpaDisableOrganizationModel,
  prefillEsiaMrpaDisableOrganization,
} from './MrpaDisableOrganization';
import MrpaDisableForeignOrganization, {
  getMrpaDisableForeignOrganizationValidation,
  mrpaDisableForeignOrganizationModel,
  prefillEsiaMrpaDisableForeignOrganization,
} from './MrpaDisableForeignOrganization';
import { prefillEsiaPersonTrustee } from '@/components/services/EgrnConfirm/Applicant/components/Person/PersonTrustee';
import { prefillEsiaAssignee } from '@/components/services/EgrnConfirm/Applicant/components/Assignee';
import { SubjectFillType, TrusteeType } from '@/constants';

export default {
  name: 'MrpaDisable',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
    roleType: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
  },
  components: {
    MrpaDisablePerson,
    MrpaDisableOrganization,
    MrpaDisableForeignOrganization,
    Footer,
  },
  data() {
    return {
      StepDirection,
      leaveStep,
      StepName,
      Role,
      role: this.roleType,
      invalidFields: [],
      person: cloneDeep(mrpaDisablePersonModel),
      organization: cloneDeep(mrpaDisableOrganizationModel),
      foreignOrganization: cloneDeep(mrpaDisableForeignOrganizationModel),
      isFirstWatch: false,
    };
  },
  computed: {
    isOrganizationDisabled() {
      return this.roleType !== Role.ORGANIZATION;
    },

    isForeignOrganizationDisabled() {
      return ![Role.FOREIGN_ORGANIZATION, Role.ORGANIZATION].includes(
        this.roleType
      );
    },

    personIsTrustee() {
      return this.person.isTrustee;
    },
  },
  watch: {
    async personIsTrustee(isTrustee) {
      if (isTrustee) {
        if (this.isFirstWatch) {
          this.isFirstWatch = false;

          return;
        }

        const [trustee, assignee] = await Promise.all([
          prefillEsiaPersonTrustee(),
          prefillEsiaAssignee(),
        ]);

        this.person = deepmerge(mrpaDisablePersonModel, {
          isTrustee,
          trustee,
          assignee,
        });
      } else {
        await this.prefillEsiaPerson();
      }
    },
  },
  validations() {
    return {
      person: getMrpaDisablePersonValidation(this.person),
      organization: getMrpaDisableOrganizationValidation(this.organization),
      foreignOrganization: getMrpaDisableForeignOrganizationValidation(
        this.foreignOrganization
      ),
    };
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    touchFields() {
      this.invalidFields = [];

      this.$v[this.role].$touch();
      checkInvalid(this.$v[this.role], this.invalidFields);
    },

    async prefillDraft() {
      const applicantStore = cloneDeep(
        ServicesModule.statement.subjects.applicant
      );

      if (
        !applicantStore.person &&
        !applicantStore.organization &&
        !applicantStore.foreignOrganization
      ) {
        await this.prefillEsia();
        return;
      }

      if (applicantStore.person) {
        this.role = Role.PERSON;
        const isTrustee = Boolean(
          applicantStore.trustee || applicantStore.assignee
        );

        if (isTrustee) {
          this.isFirstWatch = true;
        }

        this.person = deepmerge.all([
          mrpaDisablePersonModel,
          applicantStore.person,
          {
            isTrustee,
            trusteeType: applicantStore.trustee
              ? TrusteeType.TRUSTEE
              : TrusteeType.ASSIGNEE,
            trustee: applicantStore.trustee ?? mrpaDisablePersonModel.trustee,
            assignee:
              applicantStore.assignee ?? mrpaDisablePersonModel.assignee,
          },
        ]);
      }

      if (applicantStore.organization) {
        this.role = Role.ORGANIZATION;
        this.organization = deepmerge.all([
          mrpaDisableOrganizationModel,
          applicantStore.organization,
          {
            trusteeType: applicantStore.trustee
              ? TrusteeType.TRUSTEE
              : TrusteeType.ASSIGNEE,
            trustee:
              applicantStore.trustee ?? mrpaDisableOrganizationModel.trustee,
            assignee:
              applicantStore.assignee ?? mrpaDisableOrganizationModel.assignee,
          },
        ]);
      }

      if (applicantStore.foreignOrganization) {
        this.role = Role.FOREIGN_ORGANIZATION;
        this.foreignOrganization = deepmerge.all([
          mrpaDisableForeignOrganizationModel,
          applicantStore.foreignOrganization,
          {
            trusteeType: applicantStore.trustee
              ? TrusteeType.TRUSTEE
              : TrusteeType.ASSIGNEE,
            trustee:
              applicantStore.trustee ??
              mrpaDisableForeignOrganizationModel.trustee,
            assignee:
              applicantStore.assignee ??
              mrpaDisableForeignOrganizationModel.assignee,
          },
        ]);
      }
    },

    async prefillEsia() {
      if (this.roleType === Role.PERSON) {
        await this.prefillEsiaPerson();
      } else if (this.roleType === Role.ORGANIZATION) {
        await Promise.all([
          this.prefillEsiaOrganization(),
          this.prefillEsiaForeignOrganization(),
        ]);
      } else if (this.roleType === Role.FOREIGN_ORGANIZATION) {
        await this.prefillEsiaForeignOrganization();
      }
    },

    async prefillEsiaPerson() {
      this.person = await prefillEsiaMrpaDisablePerson();
    },

    async prefillEsiaOrganization() {
      this.organization = await prefillEsiaMrpaDisableOrganization();
    },

    async prefillEsiaForeignOrganization() {
      this.foreignOrganization = await prefillEsiaMrpaDisableForeignOrganization();
    },

    assemblyModel() {
      const applicant = {
        uiContext: {},
      };

      applicant[this.role] = cloneDeep(this[this.role]);
      delete applicant[this.role].trustee;
      delete applicant[this.role].assignee;

      if ([Role.ORGANIZATION, Role.FOREIGN_ORGANIZATION].includes(this.role)) {
        if (this[this.role].trusteeType === TrusteeType.TRUSTEE) {
          applicant.uiContext.fillType = SubjectFillType.MANUAL;
          applicant.trustee = cloneDeep(this[this.role]).trustee;

          if (
            applicant.trustee.documents.authorityDocument.type ===
            DocumentType.NOTARY_POWER_ATTORNEY
          ) {
            applicant.trustee.documents.authorityDocument.name = null;
            applicant.trustee.documents.authorityDocument.issuer = null;
            applicant.trustee.documents.authorityDocument.attestation = null;
          } else {
            applicant.trustee.documents.authorityDocument.regNumber = null;
          }

          return applicant;
        }

        applicant.uiContext.fillType = SubjectFillType.ASSIGNEE;
        applicant.assignee = cloneDeep(this[this.role].assignee);

        return applicant;
      }

      applicant.uiContext.fillType = SubjectFillType.MANUAL;
      if (this.person.isTrustee) {
        if (this.person.trusteeType === TrusteeType.TRUSTEE) {
          applicant.trustee = cloneDeep(this.person.trustee);

          return applicant;
        }

        applicant.uiContext.fillType = SubjectFillType.ASSIGNEE;
        applicant.assignee = cloneDeep(this.person.assignee);

        return applicant;
      }

      return applicant;
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: StepName.S1,
      });
    },
  },
};
</script>
